// ATM-T3 (2026-06-09) — Página admin pra cadastrar dados técnicos
// dos usuários (CREA, RNP, formação, título) usados nos atestados.
// Suporta usuários do sistema (Cognito) E externos (email manual).

import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  App as AntApp, Table, Button, Modal, Input, Form, Space, Typography,
  Tag, message, Flex, Select, Radio,
} from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { API_URL } from '../config';
import { fetchAuthSession } from 'aws-amplify/auth';

const { Title, Text } = Typography;

async function getToken(): Promise<string> {
  const s = await fetchAuthSession();
  return s.tokens?.idToken?.toString() || '';
}

interface DadosTec {
  email: string;
  nome_completo: string | null;
  formacao: string | null;
  titulo_profissional: string | null;
  registro_crea: string | null;
  rnp: string | null;
  cpf: string | null;
  observacoes: string | null;
  atualizado_em: string;
  eh_usuario_sistema?: boolean;
}

interface UsuarioCognito {
  email: string;
  nome: string;
  ja_cadastrado: boolean;
}

function AdminDadosTecnicosInner() {
  const [lista, setLista] = useState<DadosTec[]>([]);
  const [cognitoUsers, setCognitoUsers] = useState<UsuarioCognito[]>([]);
  const [loading, setLoading] = useState(false);
  const [editando, setEditando] = useState<DadosTec | null>(null);
  const [salvando, setSalvando] = useState(false);

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const [r1, r2] = await Promise.all([
        axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'op_usuario_dados_tecnicos_listar' },
          { headers: { Authorization: token } }),
        axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'op_usuario_dados_tecnicos_listar_cognito' },
          { headers: { Authorization: token } }),
      ]);
      const d1 = r1.data.body ? JSON.parse(r1.data.body) : r1.data;
      const d2 = r2.data.body ? JSON.parse(r2.data.body) : r2.data;
      setLista(Array.isArray(d1) ? d1 : []);
      setCognitoUsers(Array.isArray(d2) ? d2 : []);
    } catch (e: any) {
      message.error('Falha ao carregar: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }
  useEffect(() => { carregar(); }, []);

  async function salvar(values: any) {
    if (!editando) return;
    setSalvando(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_usuario_dados_tecnicos_upsert', ...values, email: editando.email || values.email },
        { headers: { Authorization: token } });
      message.success('Salvo');
      setEditando(null);
      carregar();
    } catch (e: any) {
      message.error('Falha: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  function adicionarNovo() {
    setEditando({
      email: '', nome_completo: null, formacao: null,
      titulo_profissional: null, registro_crea: null, rnp: null,
      cpf: null, observacoes: null, atualizado_em: '',
    });
  }

  const colunas = [
    {
      title: 'Tipo', dataIndex: 'eh_usuario_sistema', key: 'tipo', width: 110,
      render: (v: boolean) => v
        ? <Tag color="green" style={{ fontSize: 11 }}>🟢 Sistema</Tag>
        : <Tag style={{ fontSize: 11 }}>⚪ Externo</Tag>,
      filters: [
        { text: 'Usuário do sistema', value: true },
        { text: 'Externo', value: false },
      ],
      onFilter: (val: any, row: DadosTec) => (row.eh_usuario_sistema || false) === val,
    },
    {
      title: 'E-mail', dataIndex: 'email', key: 'email', width: 220,
      render: (v: string) => <Text strong style={{ fontSize: 12 }}>{v}</Text>,
    },
    {
      title: 'Nome completo', dataIndex: 'nome_completo', key: 'nome',
      render: (v: string | null) => v || <Text type="secondary" italic>—</Text>,
    },
    {
      title: 'Título', dataIndex: 'titulo_profissional', key: 'titulo', width: 180,
      render: (v: string | null) => v || <Text type="secondary" italic>—</Text>,
    },
    {
      title: 'CREA', dataIndex: 'registro_crea', key: 'crea', width: 140,
      render: (v: string | null) => v ? <Tag color="blue">{v}</Tag> : <Text type="secondary" italic>—</Text>,
    },
    {
      title: 'RNP', dataIndex: 'rnp', key: 'rnp', width: 110,
      render: (v: string | null) => v || <Text type="secondary" italic>—</Text>,
    },
    {
      title: '', width: 80, key: 'acoes',
      render: (_: any, row: DadosTec) => (
        <Button size="small" icon={<EditOutlined />} type="link"
          onClick={() => setEditando(row)}>Editar</Button>
      ),
    },
  ];

  const totalSistema = lista.filter(l => l.eh_usuario_sistema).length;
  const totalExterno = lista.length - totalSistema;
  const cognitoSemCadastro = cognitoUsers.filter(u => !u.ja_cadastrado).length;

  return (
    <div style={{ padding: 16 }}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={8} style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          Dados técnicos dos usuários
        </Title>
        <Button type="primary" onClick={adicionarNovo}>+ Adicionar</Button>
      </Flex>

      <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
        Cadastre aqui os dados profissionais (formação, CREA, RNP) usados em atestados técnicos.
        Pode ser um <strong>usuário do sistema</strong> (Cognito) ou alguém <strong>externo</strong>
        {' '}(subcontratado, profissional não-Hydrodata).
        {' '}<Tag color="green" style={{ fontSize: 10 }}>🟢 {totalSistema} sistema</Tag>
        <Tag style={{ fontSize: 10 }}>⚪ {totalExterno} externo</Tag>
        {cognitoSemCadastro > 0 && (
          <Tag color="orange" style={{ fontSize: 10 }}>
            {cognitoSemCadastro} usuário(s) do sistema ainda sem dados
          </Tag>
        )}
      </Text>

      <Table size="small" rowKey="email" columns={colunas as any} dataSource={lista}
        loading={loading} pagination={{ pageSize: 30 }} />

      {editando && (
        <ModalEditar dados={editando} cognitoUsers={cognitoUsers}
          onCancel={() => setEditando(null)}
          onSalvar={salvar} salvando={salvando} />
      )}
    </div>
  );
}

function ModalEditar({ dados, cognitoUsers, onCancel, onSalvar, salvando }: {
  dados: DadosTec;
  cognitoUsers: UsuarioCognito[];
  onCancel: () => void;
  onSalvar: (values: any) => Promise<void>;
  salvando: boolean;
}) {
  const [form] = Form.useForm();
  const novo = !dados.atualizado_em;
  // Modo só importa quando é cadastro novo. Em edição, o email já vem fixo.
  const [modo, setModo] = useState<'sistema' | 'externo'>('sistema');

  async function handleOk() {
    try {
      const values = await form.validateFields();
      // Quando escolhe usuário do sistema via Select, preenche email automaticamente
      if (novo && modo === 'sistema' && values._usuario_cognito) {
        values.email = values._usuario_cognito;
        // Se ainda não preencheu nome, usa o do Cognito
        if (!values.nome_completo) {
          const u = cognitoUsers.find(c => c.email === values._usuario_cognito);
          if (u?.nome) values.nome_completo = u.nome;
        }
      }
      delete values._usuario_cognito;
      await onSalvar(values);
    } catch (_) { /* validate falhou */ }
  }

  // Sem cadastro ainda (filtrados pra Select)
  const cognitoDisponiveis = cognitoUsers.filter(u => !u.ja_cadastrado);

  return (
    <Modal open onCancel={onCancel} onOk={handleOk}
      title={novo ? 'Adicionar pessoa' : `Editar — ${dados.email}`}
      okText="Salvar" cancelText="Cancelar" confirmLoading={salvando}
      width={620} destroyOnClose
      maskClosable={false}>
      <Form form={form} layout="vertical" size="small" initialValues={dados}>
        {novo && (
          <>
            <Form.Item label="Tipo de cadastro" style={{ marginBottom: 8 }}>
              <Radio.Group value={modo} onChange={(e) => setModo(e.target.value)}
                buttonStyle="solid" size="small">
                <Radio.Button value="sistema">🟢 Usuário do sistema</Radio.Button>
                <Radio.Button value="externo">⚪ Pessoa externa</Radio.Button>
              </Radio.Group>
            </Form.Item>

            {modo === 'sistema' ? (
              <Form.Item label="Selecionar usuário do sistema" name="_usuario_cognito"
                rules={[{ required: true, message: 'Escolha um usuário' }]}
                extra={cognitoDisponiveis.length === 0
                  ? 'Todos os usuários Cognito já têm cadastro. Mude pra "Pessoa externa".'
                  : `${cognitoDisponiveis.length} usuário(s) sem cadastro de dados técnicos`}>
                <Select showSearch placeholder="Buscar por nome ou e-mail…"
                  optionFilterProp="label" allowClear
                  options={cognitoDisponiveis.map(u => ({
                    value: u.email,
                    label: u.nome ? `${u.nome} (${u.email})` : u.email,
                  }))} />
              </Form.Item>
            ) : (
              <Form.Item label="E-mail (pessoa externa — qualquer e-mail válido)" name="email"
                rules={[{ required: true, type: 'email', message: 'E-mail válido obrigatório' }]}
                extra="Use o e-mail real da pessoa, ou um identificador único (ex: rt-externo+nome@empresa.com).">
                <Input placeholder="exemplo@empresa.com" />
              </Form.Item>
            )}
          </>
        )}

        <Form.Item label="Nome completo" name="nome_completo">
          <Input placeholder="Ex: Maria da Silva Santos" />
        </Form.Item>
        <Form.Item label="Formação" name="formacao">
          <Input placeholder="Ex: Engenharia Civil — UFRGS, 2015" />
        </Form.Item>
        <Form.Item label="Título profissional" name="titulo_profissional">
          <Input placeholder="Ex: Engenheira Civil" />
        </Form.Item>
        <Space.Compact style={{ width: '100%' }} block>
          <Form.Item label="Registro CREA" name="registro_crea" style={{ flex: 1 }}>
            <Input placeholder="Ex: CREA-RS 123456" />
          </Form.Item>
          <Form.Item label="RNP" name="rnp" style={{ flex: 1, marginLeft: 8 }}>
            <Input placeholder="Ex: 1234567890" />
          </Form.Item>
        </Space.Compact>
        <Form.Item label="CPF" name="cpf">
          <Input placeholder="000.000.000-00" />
        </Form.Item>
        <Form.Item label="Observações" name="observacoes">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default function AdminDadosTecnicos() {
  return <AntApp><AdminDadosTecnicosInner /></AntApp>;
}
